import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	View,
	ActivityIndicator,
	Modal,
	TouchableOpacity,
	ScrollView,
	Pressable,
	Alert,
} from 'react-native';
import Toast from 'react-native-root-toast';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Text from '../text/Text';
import { useOfflineQueue } from '../../offline';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useFieldTabBarHeight } from '../navigation/fieldTabBarHeight';
import { setSyncBannerHeight, useScreenFooterHeight } from '../../design/screenFooterHeight';
import { vibrateConfirm } from '../../utils/feedback';
import { getFullMachineName } from '../../../common/utils/machine';
import { formatDate } from '../../../common/utils/utils';
import { POS_TYPE } from '../../../common/constants';
import { createSyncStatusBannerStyles } from './SyncStatusBanner.styles';

const SyncStatusBanner = () => {
	const { queue, isOnline, isReady, flush, isFlushing, remove, resetFailures } = useOfflineQueue();
	const styles = useThemedStyles(createSyncStatusBannerStyles);
	// The field tab bar and the focused redesigned Screen's footer (dock, tab
	// bar) publish their heights so the banner sits above them. Both sit inside
	// the safe-area bottom padding, so that inset is added too.
	const tabBarHeight = useFieldTabBarHeight();
	const screenFooterHeight = useScreenFooterHeight();
	const { bottom: bottomInset } = useSafeAreaInsets();
	const bottomChromeHeight = tabBarHeight + screenFooterHeight;
	const previousLengthRef = useRef(queue.length);
	const previousOnlineRef = useRef(isOnline);
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);
	const [isManualSyncing, setIsManualSyncing] = useState(false);
	const [isClearingFailed, setIsClearingFailed] = useState(false);
	const [isClearingAll, setIsClearingAll] = useState(false);
	const isSyncing = isManualSyncing || isFlushing;
	// Used to suppress redundant banner toasts when the queue is cleared manually
	const skipNextClearedToastRef = useRef(false);

	useEffect(() => {
		if (!isReady) {
			return;
		}

		const currentLength = queue.length;
		const previousLength = previousLengthRef.current;

		if (previousLength > 0 && currentLength === 0 && isOnline) {
			if (!skipNextClearedToastRef.current) {
				vibrateConfirm();
				Toast.show('All pending updates synced');
			}
			skipNextClearedToastRef.current = false;
		}

		previousLengthRef.current = currentLength;
		previousOnlineRef.current = isOnline;
	}, [queue.length, isOnline, isReady]);

	const queueItems = useMemo(() => {
		return [...queue]
			.map(entry => ({
				...entry,
				failureCount: entry.failureCount || 0,
				lastError: entry.lastError || null,
			}))
			.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
	}, [queue]);

	const shouldShowBanner = isReady && queue.length > 0;

	const bannerMessage = useMemo(() => {
		const count = queue.length;
		const countLabel = `${count} pending ${count === 1 ? 'update' : 'updates'}`;

		if (queueItems.some(entry => entry.failureCount > 0)) {
			return `${countLabel} ${count === 1 ? 'needs' : 'need'} attention`;
		}

		if (!isOnline) {
			return `${countLabel} waiting for connection`;
		}

		return isSyncing ? `Syncing ${countLabel}...` : `${countLabel} waiting to sync`;
	}, [queue.length, isOnline, isSyncing, queueItems]);

	const hasFailedItems = useMemo(() => queueItems.some(entry => entry.failureCount > 0), [queueItems]);
	const failedItems = useMemo(() => queueItems.filter(entry => entry.failureCount > 0), [queueItems]);
	const hintText = hasFailedItems ? 'Tap to review or remove' : 'Tap for details or clear';

	const getEntryLabel = useCallback((entry) => {
		switch (entry.configKey) {
			case 'createTicket':
				return 'Ticket creation';
			case 'updateTicketPhotos':
				return 'Ticket photo upload';
			case 'confirmStockLevels':
				return 'Stock confirmation';
			case 'confirmMachineValues':
				return 'Values confirmation';
			case 'reportIncorrectStockSetting':
				return 'Setting report';
			case 'confirmMyDayStep':
				return 'My day step';
			case 'manualRestock':
				return 'Restock';
			case 'createMedia':
				return 'Photo upload';
			case 'markServiceDocketCouldNotComplete':
				return 'Couldn\'t complete';
			case 'clearServiceDocketCouldNotComplete':
				return 'Couldn\'t complete undone';
			default:
				return 'Pending update';
		}
	}, []);

	const getEntryMeta = useCallback((entry) => {
		const { meta = {}, variables = {} } = entry;
		const machine = meta.machine ||
			variables?.input?.machineDetails ||
			variables?.machineDetails ||
			variables?.machine ||
			null;
		const fallbackMachineName = meta.fallbackMachineName ||
			variables?.input?.machineName ||
			variables?.machineName ||
			null;
		const formInput = meta.formInput || variables?.input || {};
		const machineId = meta.machineId ||
			formInput.machine ||
			variables?.machine ||
			variables?.input?.machineId;

		let baseMachineName = '';
		if (machine) {
			baseMachineName = getFullMachineName(machine, machine?.place, machine?.location);
		}
		else if (fallbackMachineName) {
			baseMachineName = fallbackMachineName;
		}
		else if (machineId) {
			baseMachineName = `#${machineId}`;
		}

		let machineLabel = baseMachineName;
		if (machine) {
			const prefix = machine.type === POS_TYPE.MARKET ? 'Market' : 'Machine';
			machineLabel = baseMachineName ? `${prefix} ${baseMachineName}` : prefix;
		}
		else if (!baseMachineName && machineId) {
			machineLabel = `Machine ${machineId}`;
		}

		if (meta.stepLabel) {
			return meta.stepLabel;
		}
		if (meta.ticketId) {
			return machineLabel ? `${machineLabel} • Ticket ${meta.ticketId}` : `Ticket ${meta.ticketId}`;
		}
		if (meta.docketId || meta.serviceDocketId) {
			if (machineLabel) {
				return machineLabel;
			}
			const docketLabel = `Docket ${meta.docketId || meta.serviceDocketId}`;
			return docketLabel;
		}
		if (machineLabel) {
			return machineLabel;
		}
		return 'Queued update';
	}, []);

	const handleOpenDetails = useCallback(() => {
		setIsDetailsOpen(true);
	}, []);

	const handleCloseDetails = useCallback(() => {
		setIsDetailsOpen(false);
	}, []);

	const handleSyncNow = useCallback(async () => {
		if (!isOnline) {
			Toast.show('Connect to the internet to sync pending updates');
			return;
		}

		if (isManualSyncing || isFlushing) {
			Toast.show('Already syncing pending updates');
			return;
		}

		setIsManualSyncing(true);
		Toast.show('Attempting to sync pending updates...');
		try {
			// A manual sync retries items paused after repeated failures too.
			await resetFailures();
			await flush();
		}
		catch (error) {
			Toast.show('Unable to start sync right now');
		}
		finally {
			setIsManualSyncing(false);
		}
	}, [flush, isOnline, isManualSyncing, isFlushing, resetFailures]);

	// Allow drivers to clear stuck items so the banner can be dismissed
	const handleClearFailed = useCallback(() => {
		if (!failedItems.length || isClearingFailed || isSyncing) {
			return;
		}

		Alert.alert(
			'Remove pending updates?',
			'These items failed to sync. Removing them will discard the unsent changes.',
			[
				{ text: 'Cancel', style: 'cancel' },
				{
					text: 'Remove',
					style: 'destructive',
					onPress: async () => {
						setIsClearingFailed(true);
						skipNextClearedToastRef.current = true;
						try {
							for (const entry of failedItems) {
								await remove(entry.id);
							}
							Toast.show('Removed stuck pending updates');
						}
						catch (error) {
							Toast.show('Unable to remove pending updates');
						}
						finally {
							setIsClearingFailed(false);
							setIsDetailsOpen(false);
						}
					},
				},
			],
		);
	}, [failedItems, isClearingFailed, isSyncing, remove]);

	// Escape hatch to clear all pending items, even if they never flagged as failed
	const handleClearAll = useCallback(() => {
		if (!queueItems.length || isClearingAll || isSyncing) {
			return;
		}

		Alert.alert(
			'Clear all pending updates?',
			'This will discard every queued change on this device. Use only if sync is stuck.',
			[
				{ text: 'Cancel', style: 'cancel' },
				{
					text: 'Clear all',
					style: 'destructive',
					onPress: async () => {
						setIsClearingAll(true);
						skipNextClearedToastRef.current = true;
						try {
							for (const entry of queueItems) {
								await remove(entry.id);
							}
							Toast.show('Pending updates cleared on this device');
						}
						catch (error) {
							Toast.show('Unable to clear pending updates');
						}
						finally {
							setIsClearingAll(false);
							setIsDetailsOpen(false);
						}
					},
				},
			],
		);
	}, [queueItems, isClearingAll, isSyncing, remove]);

	// Screen pads its body by this height so content can scroll clear of the banner.
	useEffect(() => {
		if (!shouldShowBanner) {
			setSyncBannerHeight(0);
		}
	}, [shouldShowBanner]);
	useEffect(() => () => setSyncBannerHeight(0), []);
	const handleBannerLayout = useCallback((event) => {
		setSyncBannerHeight(event.nativeEvent.layout.height);
	}, []);

	if (!shouldShowBanner) {
		return null;
	}

	return (
		<View pointerEvents="auto" onLayout={handleBannerLayout} style={[styles.wrapper, bottomChromeHeight > 0 && { bottom: bottomInset + bottomChromeHeight + 12 }]}>
			<View style={styles.banner}>
				<Pressable style={styles.bannerContent} onPress={handleOpenDetails}>
					<View style={styles.indicator}>
						{hasFailedItems ? (
							<Text style={[styles.indicatorColor, styles.indicatorWarning]}>!</Text>
						) : (isSyncing ? <ActivityIndicator size="small" color={styles.indicatorColor.color} /> : null)}
					</View>
					<View>
						<Text style={styles.text}>{bannerMessage}</Text>
						<Text style={styles.hint}>{hintText}</Text>
					</View>
				</Pressable>
				{isOnline ? (
					<TouchableOpacity
						style={[
							styles.syncButton,
							isSyncing ? styles.syncButtonDisabled : null,
						]}
						onPress={handleSyncNow}
						disabled={isSyncing}
						accessibilityState={{ disabled: isSyncing }}
					>
						<Text style={styles.syncButtonText}>{isSyncing ? 'Syncing…' : 'Sync now'}</Text>
					</TouchableOpacity>
				) : null}
			</View>

			<Modal
				visible={isDetailsOpen}
				transparent={true}
				animationType="fade"
				onRequestClose={handleCloseDetails}
			>
				<View style={styles.modalOverlay}>
					<View style={styles.modalContent}>
						<Text style={styles.modalTitle}>Pending sync details</Text>
						{hasFailedItems ? (
							<Text style={styles.warningText}>Some updates failed to sync. Remove stuck items or try again.</Text>
						) : null}
						<ScrollView style={styles.modalList}>
							{queueItems.map(entry => {
								const description = getEntryMeta(entry);
								const createdLabel = entry.createdAt ? formatDate(entry.createdAt, 'DAY_MONTHWORD_YEAR_HOUR_MINUTE_AMPM') : '';
								return (
									<View key={entry.id} style={styles.modalItem}>
										<Text style={styles.modalItemTitle}>{getEntryLabel(entry)}</Text>
										{description ? <Text style={styles.modalItemMeta}>{description}</Text> : null}
										{entry.failureCount > 0 ? (
											<Text style={styles.modalItemError}>
												{entry.lastError ? `Last error: ${entry.lastError}` : 'Last sync attempt failed'}
											</Text>
										) : null}
										{createdLabel ? <Text style={styles.modalItemTimestamp}>{createdLabel}</Text> : null}
									</View>
								);
							})}
						</ScrollView>
						<View style={styles.modalButtonRow}>
							{hasFailedItems ? (
								<TouchableOpacity
									style={[
										styles.modalClearButton,
										isClearingFailed || isSyncing ? styles.syncButtonDisabled : null,
									]}
									onPress={isClearingFailed || isSyncing ? undefined : handleClearFailed}
									disabled={isClearingFailed || isSyncing}
									accessibilityState={{ disabled: isClearingFailed || isSyncing }}
								>
									<Text style={styles.modalClearButtonText}>
										{isClearingFailed ? 'Removing…' : 'Remove failed'}
									</Text>
								</TouchableOpacity>
							) : null}
							<TouchableOpacity
								style={[
									styles.modalClearAllButton,
									isClearingAll || isSyncing ? styles.syncButtonDisabled : null,
								]}
								onPress={isClearingAll || isSyncing ? undefined : handleClearAll}
								disabled={isClearingAll || isSyncing}
								accessibilityState={{ disabled: isClearingAll || isSyncing }}
							>
								<Text style={styles.modalClearAllButtonText}>
									{isClearingAll ? 'Clearing…' : 'Clear all'}
								</Text>
							</TouchableOpacity>
							<TouchableOpacity
								style={[
									styles.modalSyncButton,
									(!isOnline || isSyncing) ? styles.syncButtonDisabled : null,
								]}
								onPress={handleSyncNow}
								disabled={!isOnline || isSyncing}
								accessibilityState={{ disabled: !isOnline || isSyncing }}
							>
								<Text style={styles.modalSyncButtonText}>
									{!isOnline ? 'Connect to sync' : (isSyncing ? 'Syncing…' : 'Sync now')}
								</Text>
							</TouchableOpacity>
							<TouchableOpacity style={styles.modalCloseButton} onPress={handleCloseDetails}>
								<Text style={styles.modalCloseText}>Close</Text>
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</Modal>
		</View>
	);
};

export default SyncStatusBanner;
